import React, { useState } from "react";
import { motion } from "framer-motion";
import { SiGmail, SiWhatsapp, SiGooglemaps, SiLinkedin, SiGithub, SiInstagram } from "react-icons/si";
import PageShell, { Reveal } from "../components/PageShell";
import { profile } from "../data/profile";
import "../styles/Contact.scss";

// Opsional: isi REACT_APP_FORMSPREE_ID di file .env agar form terkirim langsung ke email.
// Jika kosong, form akan membuka aplikasi email pengunjung dengan pesan yang sudah terisi.
const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

const channels = [
  { icon: SiGmail, label: "Email", value: profile.email, href: `mailto:${profile.email}` },
  { icon: SiWhatsapp, label: "WhatsApp", value: profile.phone, href: profile.whatsapp, external: true },
  { icon: SiLinkedin, label: "LinkedIn", value: "in/raihan-aldy", href: profile.linkedin, external: true },
  { icon: SiGithub, label: "GitHub", value: `@${profile.githubUser}`, href: profile.github, external: true },
  { icon: SiInstagram, label: "Instagram", value: "@rhnaldy_", href: profile.instagram, external: true },
  { icon: SiGooglemaps, label: "Location", value: profile.locationShort, href: null },
];

const empty = { name: "", email: "", subject: "", message: "" };

function Contact() {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = `mailto:${profile.email}`;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    if (!FORMSPREE_ID) {
      const body = `${form.message}\n\n— ${form.name} (${form.email})`;
      window.location.href = `mailto:${profile.email}?subject=${encodeURIComponent(
        form.subject
      )}&body=${encodeURIComponent(body)}`;
      setStatus({
        type: "info",
        message: "Your email app should open with the message ready — just hit send.",
      });
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus({ type: "success", message: "Message sent — I'll get back to you soon." });
      setForm(empty);
    } catch {
      setStatus({
        type: "error",
        message: `Couldn't send the message. Please email me directly at ${profile.email}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell slug="contact">
      <section className="contact-hero">
        <Reveal>
          <span className="eyebrow">Open to collaboration</span>
          <h2 className="contact-headline">
            Got a system to build? <br />
            <span>Let's talk.</span>
          </h2>
        </Reveal>
        <Reveal className="contact-email-row" delay={0.1}>
          <a href={`mailto:${profile.email}`} className="contact-email">
            {profile.email}
          </a>
          <button type="button" className="btn btn-ghost" onClick={copyEmail}>
            {copied ? "Copied ✓" : "Copy email"}
          </button>
        </Reveal>
      </section>

      <section className="contact-grid">
        <div className="channel-list">
          {channels.map(({ icon: Icon, label, value, href, external }, i) => {
            const inner = (
              <>
                <span className="channel-icon">
                  <Icon aria-hidden="true" />
                </span>
                <span className="channel-text">
                  <span className="channel-label">{label}</span>
                  <span className="channel-value">{value}</span>
                </span>
                {href && (
                  <span className="channel-arrow" aria-hidden="true">
                    ↗
                  </span>
                )}
              </>
            );
            return (
              <Reveal key={label} delay={i * 0.04}>
                {href ? (
                  <a
                    className="card channel"
                    href={href}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {inner}
                  </a>
                ) : (
                  <div className="card channel">{inner}</div>
                )}
              </Reveal>
            );
          })}
        </div>

        <Reveal className="card contact-form-card" delay={0.1}>
          <h3>Send a message</h3>
          <form className="contact-form" onSubmit={onSubmit}>
            <div className="form-row">
              <label>
                <span>Name</span>
                <input name="name" value={form.name} onChange={onChange} placeholder="Your name" required />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@company.com"
                  required
                />
              </label>
            </div>
            <label>
              <span>Subject</span>
              <input
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder="Project, role, or collaboration"
                required
              />
            </label>
            <label>
              <span>Message</span>
              <textarea
                name="message"
                rows={6}
                value={form.message}
                onChange={onChange}
                placeholder="Tell me what you're building…"
                required
              />
            </label>

            {status.message && (
              <motion.p
                className={`form-status is-${status.type}`}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                role="status"
              >
                {status.message}
              </motion.p>
            )}

            <button type="submit" className="btn btn-primary" disabled={sending}>
              {sending ? "Sending…" : "Send message"} <span aria-hidden="true">→</span>
            </button>
          </form>
        </Reveal>
      </section>
    </PageShell>
  );
}

export default Contact;
